import { useEffect, useRef, useState } from "react";
import type { Strings } from "../i18n/strings";
import type { InputMode, Lang, RawSignals } from "../lib/types";
import { InputTracker } from "../lib/collect";
import { playChirp, warmupSound } from "../lib/sound";
import {
  startVoiceCapture,
  stopVoiceCapture,
  releaseVoiceCapture,
  type VoiceCapture,
} from "../lib/voiceMetrics";
import { serverTranscribe } from "../lib/sttClient";
import Logo from "./Logo";
import VoiceViz from "./VoiceViz";

const LANGS: Lang[] = ["ko", "en", "ja"];

// 여러 번 마이크를 껐다 켰을 때 음성 지표를 누적 병합(보통은 한 세션).
function mergeVoice(a: VoiceCapture, b: VoiceCapture): VoiceCapture {
  const total = a.durationMs + b.durationMs || 1;
  const wA = a.durationMs / total;
  const wB = b.durationMs / total;
  return {
    peakVolume: Math.max(a.peakVolume, b.peakVolume),
    pitchVariance: a.pitchVariance * wA + b.pitchVariance * wB,
    volumeTimeline: [...a.volumeTimeline, ...b.volumeTimeline],
    pauseRatio: a.pauseRatio * wA + b.pauseRatio * wB,
    pauseCount: a.pauseCount + b.pauseCount,
    durationMs: a.durationMs + b.durationMs,
  };
}

// 준비 조건: 한 글자라도 있으면 제출 가능(짧으면 점수가 낮게 나올 뿐).
function isReady(text: string): boolean {
  return text.trim().length >= 1;
}

interface Props {
  t: Strings;
  lang: Lang;
  setLang: (l: Lang) => void;
  onSettings: () => void;
  onSubmit: (signals: RawSignals) => void;
}

// ★음성 입력 = 녹음 후 전사(§4). 마이크를 누르면 getUserMedia 한 스트림으로 음량 지표 + 오디오 녹음을
//   병행하고(STT 플러그인 미사용 → 동시 마이크 충돌·크래시 없음), 마이크를 끄면 오디오를 서버로 보내
//   받아쓰기한다. 음량/음높이/멈춤은 점수·곡선·유형·코멘트로 흘러가 음성≠텍스트 결과가 된다.
export default function Vent({ t, lang, setLang, onSettings, onSubmit }: Props) {
  const [text, setText] = useState(() => localStorage.getItem("bwek_draft") ?? "");
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const trackerRef = useRef<InputTracker>(new InputTracker());
  const taRef = useRef<HTMLTextAreaElement>(null);
  const voiceRef = useRef<VoiceCapture | null>(null); // 누적 음성 지표(있으면 mode=voice/mixed)
  const typedRef = useRef<boolean>((localStorage.getItem("bwek_draft") ?? "").trim().length > 0); // 직접 타이핑 여부
  const ready = isReady(text);

  // 초안 지속(제출 시 App에서 삭제).
  useEffect(() => {
    localStorage.setItem("bwek_draft", text);
  }, [text]);

  // 화면을 떠날 때(제출→구겨버리기 등) 마이크 캡처를 확실히 해제 — 누수 방지.
  useEffect(() => {
    return () => releaseVoiceCapture();
  }, []);

  function update(next: string) {
    trackerRef.current.onChange(next);
    setText(next);
  }
  // 손으로 직접 친 변경(전사 삽입과 구분). textarea onChange에서만 호출.
  function manualUpdate(next: string) {
    typedRef.current = true;
    update(next);
  }
  // 전사 텍스트를 현재 글 끝에 붙인다(녹음 중 타이핑이 있어도 안전 — 최신 글 기준).
  function appendTranscript(chunk: string) {
    if (!chunk) return;
    setText((cur) => {
      const next = cur ? `${cur} ${chunk}` : chunk;
      trackerRef.current.onChange(next);
      return next;
    });
  }

  async function toggleMic() {
    if (transcribing) return; // 전사 중엔 토글 막기

    if (listening) {
      // 녹음 종료 → 지표 누적 + 서버 전사.
      playChirp(false);
      setListening(false);
      const result = await stopVoiceCapture();
      if (!result) return;
      voiceRef.current = voiceRef.current ? mergeVoice(voiceRef.current, result.metrics) : result.metrics;
      setTranscribing(true);
      const said = await serverTranscribe(result.blob, lang);
      setTranscribing(false);
      appendTranscript(said);
      return;
    }

    // 녹음 시작(getUserMedia + 음량 분석 + MediaRecorder). 권한 거부 등 실패면 조용히 무시.
    const ok = await startVoiceCapture();
    if (!ok) return;
    playChirp(true);
    setListening(true);
  }

  function submit() {
    if (!ready) return;
    warmupSound(); // 제스처 중 오디오 컨텍스트 깨움 → 구겨버리기 크런치 지연 방지
    releaseVoiceCapture(); // 녹음 중이면 마이크 해제(진행 중 녹음은 버림 — 마이크 끄고 제출 권장)

    // 모드 판정: 음성 신호 있음 + 직접 타이핑 → mixed, 음성만 → voice, 그 외 → text.
    const v = voiceRef.current;
    const hasVoice = !!v && v.volumeTimeline.length > 0;
    const mode: InputMode = hasVoice ? (typedRef.current ? "mixed" : "voice") : "text";
    onSubmit(trackerRef.current.finalize(text, mode, v));
  }

  const micLabel = transcribing
    ? `● ${t.transcribing}`
    : listening
      ? `● ${t.listening}`
      : `🎤 ${t.micHint}`;

  return (
    <div
      className="screen vent"
      // 텍스트 영역 '밖'을 누르면 키보드 닫기(blur). 안을 누르면 그대로 커서 이동.
      onPointerDown={(e) => {
        if (e.target !== taRef.current) taRef.current?.blur();
      }}
    >
      <div className="vent-header">
        <Logo lang={lang} height={20} stroke={30} />
        <div className="vent-tools">
          <div className="lang-toggle-row">
            {LANGS.map((L, i) => (
              <span key={L}>
                {i > 0 && <span className="sep">/</span>}
                <button
                  className={`lang-opt ${lang === L ? "on" : ""}`}
                  onClick={() => setLang(L)}
                >
                  {L.toUpperCase()}
                </button>
              </span>
            ))}
          </div>
          <button className="gear" onClick={onSettings} aria-label="settings">
            ⚙
          </button>
        </div>
      </div>

      <div className="note">
        <div className="note-label">{t.label}</div>
        <textarea
          ref={taRef}
          className="note-area"
          value={text}
          placeholder={t.placeholder}
          // 포커스 = 입력 준비 → 머뭇거림(startDelay) 기준을 여기로 리셋(화면 띄워두고 늦게 쳐도 안 부풀게).
          onFocus={() => trackerRef.current.markReady()}
          // 삭제/삽입은 finalize에서 '안정 길이' 증감으로 산출(IME 조합 churn 무시) → 여기선 값만 넘긴다.
          onChange={(e) => manualUpdate(e.target.value)}
        />
      </div>

      <p className="reassure">{t.reassure}</p>

      {listening && <VoiceViz />}

      <button
        className={`mic ${listening || transcribing ? "on" : ""}`}
        onClick={toggleMic}
        disabled={transcribing}
      >
        {micLabel}
      </button>

      <button className="submit-btn" onClick={submit} disabled={!ready}>
        {ready ? t.submit : t.keepGoing}
      </button>
    </div>
  );
}
